import { WEEKDAYS } from '@/constants/weekdays';
import { HotelResponse } from '@/types/benefits/hotel/HotelResponse';
import { getHotelCategoryLabel } from './getHotelCategoryName';
import { CURRENCIES } from '../../../../shared/CountriesData';
import {
  getRoomPensionOptions,
  toHotelPensionType,
} from '@/constants/hotelPension';
import { numberIfNotUndefined } from '../prices/numberIfNotUndefined';
import { buildEmptyPensionCard } from './hotel/buildEmptyPensionCard';
import {
  buildEmptyHotelSupplement,
  buildEmptyHotelSupplementPeriod,
} from './hotel/buildEmptyHotelSupplementCard';
import { Translator } from '@/types/translator.type';
import { SupportedLocale } from '@/types/supported-locale.type';
import { LOCALES } from '@/i18n/config';

const initializeHotelTranslations = (hotel: HotelResponse) => {
  const translations = {} as Record<
    SupportedLocale,
    {
      shortDescriptionTitle?: string;
      shortDescriptionBody?: string;
      longDescriptionTitle?: string;
      longDescriptionBody?: string;
      benefit?: { name?: string };
      hotelRoomCategories?: {
        name?: string;
        shortDescription?: string;
        longDescription?: string;
      }[];
    }
  >;

  for (const locale of LOCALES) {
    translations[locale] = {
      shortDescriptionTitle:
        hotel.translations?.[locale]?.shortDescriptionTitle,
      shortDescriptionBody: hotel.translations?.[locale]?.shortDescriptionBody,
      longDescriptionTitle: hotel.translations?.[locale]?.longDescriptionTitle,
      longDescriptionBody: hotel.translations?.[locale]?.longDescriptionBody,
      benefit: {
        name:
          hotel.translations?.[locale]?.benefit?.name ||
          hotel.benefit.name ||
          '',
      },
      hotelRoomCategories: hotel.translations?.[locale]?.HotelRoomCategory?.map(
        (hotelRoomCategory) => ({
          name: hotelRoomCategory.name ?? undefined,
          shortDescription: hotelRoomCategory.shortDescription ?? undefined,
          longDescription: hotelRoomCategory.longDescription ?? undefined,
        }),
      ),
    };
  }

  return translations;
};

export const loadHotelDefaultValues = ({
  hotel,
  t,
}: {
  hotel: HotelResponse;
  t: Translator;
}) =>
  ({
    translations: initializeHotelTranslations(hotel),
    name: hotel.benefit.name,
    category: hotel.category
      ? {
          value: getHotelCategoryLabel(t, hotel.category),
          label: getHotelCategoryLabel(t, hotel.category),
        }
      : undefined,
    chain: hotel.chain
      ? {
          value: hotel.chain,
          label: hotel.chain,
        }
      : undefined,
    supplier: {
      label: hotel.benefit.supplier.name,
      value: hotel.benefit.supplier.id,
    },
    currency: {
      value: hotel.benefit.currency,
      label: CURRENCIES.find(
        (currency) => currency.value === hotel.benefit.currency,
      )?.label,
    },
    country: {
      label: hotel.benefit.country,
      value: hotel.benefit.country,
    },
    marginRate: numberIfNotUndefined(hotel.benefit.marginRate),
    address: hotel.address ?? undefined,
    postalCode: hotel.postalCode ?? undefined,
    city: hotel.city ? { value: hotel.city, label: hotel.city } : undefined,
    latitude: hotel.latitude ? Number(hotel.latitude) : undefined,
    longitude: hotel.longitude ? Number(hotel.longitude) : undefined,
    contacts: {
      coordinates:
        hotel.contactPhones && hotel.contactPhones.length > 0
          ? hotel.contactPhones.map((contactPhone, indexContactPhone) => ({
              name:
                hotel.contactNames &&
                hotel.contactNames.length > indexContactPhone
                  ? hotel.contactNames[indexContactPhone]
                  : undefined,
              phoneNumber: contactPhone,
            }))
          : [{ name: undefined, phoneNumber: undefined }],
      website: hotel.contactWebsite ?? undefined,
      mainEmail: hotel.contactMainEmail ?? undefined,
      reservationsEmail: hotel.contactReservationsEmail ?? undefined,
      stockAlertEmail: hotel.contactStockAlertEmail ?? undefined,
      secondaryEmails:
        hotel.contactSecondaryEmails && hotel.contactSecondaryEmails.length
          ? hotel.contactSecondaryEmails.map((email) => ({ email }))
          : [],
    },
    mainImage: hotel.mainImage ?? undefined,
    images: hotel.images ?? [],
    shortDescriptionTitle: hotel.shortDescriptionTitle ?? undefined,
    shortDescriptionBody: hotel.shortDescriptionBody ?? undefined,
    longDescriptionTitle: hotel.longDescriptionTitle ?? undefined,
    longDescriptionBody: hotel.longDescriptionBody ?? undefined,
    acceptsChildPricing: hotel.acceptsChildPricing ? 'true' : 'false',
    childAges:
      hotel.secondChildAgeFrom && hotel.secondChildAgeTo
        ? [
            {
              childAgeFrom: hotel.childAgeFrom ?? 2,
              childAgeTo: hotel.childAgeTo ?? 11,
            },
            {
              childAgeFrom: hotel.secondChildAgeFrom ?? 12,
              childAgeTo: hotel.secondChildAgeTo ?? 17,
            },
          ]
        : [
            {
              childAgeFrom: hotel.childAgeFrom ?? 2,
              childAgeTo: hotel.childAgeTo ?? 11,
            },
          ],
    pricingType: hotel.pricingType ?? 'byRoom',
    reducedPriceExists: hotel.reducedPriceExists ? 'true' : 'false',
    priceReductionType: hotel.isReduction ? 'reduction' : 'newPrice',
    pricingPensionType: (() => {
      const options = getRoomPensionOptions(t);
      const stable = toHotelPensionType(hotel.pricingPensionType);
      const option = options.find((o) => o.value === stable) ?? options[0];
      return { value: option.value, label: option.label };
    })(),
    shouldFillOtherPensionTypes: hotel.shouldFillOtherPensionTypes
      ? 'true'
      : 'false',
    areOtherPensionPricesCommon: hotel.areOtherPensionPricesCommon
      ? 'true'
      : 'false',
    commonPensionPricings: (() => {
      const arr =
        hotel.commonPensionPricings && hotel.commonPensionPricings.length > 0
          ? hotel.commonPensionPricings.map((pricing) => ({
              id: pricing.id,
              name: pricing.name ?? undefined,
              periods:
                pricing.periods?.map((period) => ({
                  id: period.id,
                  startDate: period.startDate
                    ? new Date(period.startDate)
                    : undefined,
                  endDate: period.endDate
                    ? new Date(period.endDate)
                    : undefined,
                  applicationDays: period.applicationDays ?? [],
                })) ?? [],
              prices:
                pricing.prices?.map((price) => ({
                  id: price.id,
                  pensionType: price.pensionType,
                  adultPrice: price.adultPrice ?? undefined,
                  childPrice: price.childPrice ?? undefined,
                  adolescentPrice: price.adolescentPrice ?? undefined,
                })) ?? [],
            }))
          : [buildEmptyPensionCard()];
      return hotel.areOtherPensionPricesCommon && arr.length === 0
        ? [buildEmptyPensionCard()]
        : arr;
    })(),
    shouldFillHotelSupplements: hotel.shouldFillHotelSupplements
      ? 'true'
      : 'false',
    hotelSupplements: (() => {
      const arr =
        hotel.hotelSupplements && hotel.hotelSupplements.length > 0
          ? hotel.hotelSupplements.map((supp) => ({
              id: supp.id,
              name: supp.name ?? '',
              pensionTypes: supp.pensionTypes ?? [],
              pricingList:
                supp.pricingList?.map((p) => ({
                  id: p.id,
                  name: p.name ?? '',
                  periods:
                    p.periods && p.periods.length > 0
                      ? p.periods.map((period) => ({
                          id: period.id,
                          startDate: period.startDate
                            ? typeof period.startDate === 'string'
                              ? period.startDate
                              : new Date(period.startDate)
                                  .toISOString()
                                  .slice(0, 10)
                            : null,
                          endDate: period.endDate
                            ? typeof period.endDate === 'string'
                              ? period.endDate
                              : new Date(period.endDate)
                                  .toISOString()
                                  .slice(0, 10)
                            : null,
                          applicationDays: period.applicationDays ?? [],
                        }))
                      : [buildEmptyHotelSupplementPeriod()],
                  pricing: p.pricing
                    ? {
                        id: p.pricing.id,
                        adultPrice: p.pricing.adultPrice ?? null,
                        childPrice: p.pricing.childPrice ?? null,
                        adolescentPrice: p.pricing.adolescentPrice ?? null,
                      }
                    : undefined,
                })) ?? [],
            }))
          : [buildEmptyHotelSupplement()];
      return hotel.shouldFillHotelSupplements && arr.length === 0
        ? [buildEmptyHotelSupplement()]
        : arr;
    })(),
    expertMode: hotel.expertMode ? 'true' : 'false',
    shouldAssociateBenefits:
      hotel.shouldAssociateBenefits !== undefined
        ? hotel.shouldAssociateBenefits
          ? 'true'
          : 'false'
        : hotel.associatedBenefits && hotel.associatedBenefits.length > 0
          ? 'true'
          : 'false',
    associatedBenefits:
      hotel.associatedBenefits && hotel.associatedBenefits.length > 0
        ? hotel.associatedBenefits.map((associatedBenefit) => ({
            benefitId: associatedBenefit.benefitId,
            frequency: associatedBenefit.frequency,
            benefit: associatedBenefit.benefit
              ? {
                  id: associatedBenefit.benefit.id,
                  name: associatedBenefit.benefit.name,
                  code: associatedBenefit.benefit.code,
                  type: associatedBenefit.benefit.type,
                  country: associatedBenefit.benefit.country,
                  supplier: associatedBenefit.benefit.supplier,
                  activity: associatedBenefit.benefit.activity,
                }
              : undefined,
          }))
        : [],
    hotelRoomCategories:
      hotel.HotelRoomCategory && hotel.HotelRoomCategory.length
        ? hotel.HotelRoomCategory.map((hotelRoomCategory) => ({
            category: hotelRoomCategory.category
              ? {
                  value: hotelRoomCategory.category,
                  label: hotelRoomCategory.category,
                }
              : undefined,
            complementaryInformation: hotelRoomCategory.complementaryInformation
              ? {
                  value: hotelRoomCategory.complementaryInformation,
                  label: hotelRoomCategory.complementaryInformation,
                }
              : undefined,
            name: hotelRoomCategory.name ?? undefined,
            mainImage: hotelRoomCategory.mainImage ?? undefined,
            images: hotelRoomCategory.images ?? [],
            shortDescription: hotelRoomCategory.shortDescription ?? undefined,
            longDescription: hotelRoomCategory.longDescription ?? undefined,
            categoryPensionPricings: (() => {
              const arr =
                hotelRoomCategory.categoryPensionPricings?.map((pricing) => ({
                  id: pricing.id,
                  name: pricing.name ?? undefined,
                  periods:
                    pricing.periods?.map((period) => ({
                      id: period.id,
                      startDate: period.startDate
                        ? new Date(period.startDate)
                        : undefined,
                      endDate: period.endDate
                        ? new Date(period.endDate)
                        : undefined,
                      applicationDays: period.applicationDays ?? [],
                    })) ?? [],
                  prices:
                    pricing.prices?.map((price) => ({
                      id: price.id,
                      pensionType: price.pensionType,
                      adultPrice: price.adultPrice ?? undefined,
                      childPrice: price.childPrice ?? undefined,
                      adolescentPrice: price.adolescentPrice ?? undefined,
                    })) ?? [],
                })) ?? [];
              return hotel.shouldFillOtherPensionTypes &&
                !hotel.areOtherPensionPricesCommon &&
                arr.length === 0
                ? [buildEmptyPensionCard()]
                : arr;
            })(),
            roomTypes: {
              individual: hotelRoomCategory.roomTypes.includes('individual'),
              double: hotelRoomCategory.roomTypes.includes('double'),
              triple: hotelRoomCategory.roomTypes.includes('triple'),
              quadruple: hotelRoomCategory.roomTypes.includes('quadruple'),
            },
            individualRoomOptions: hotelRoomCategory.individualRoomOptions,
            doubleRoomOptions: hotelRoomCategory.doubleRoomOptions,
            tripleRoomOptions: hotelRoomCategory.tripleRoomOptions,
            quadrupleRoomOptions: hotelRoomCategory.quadrupleRoomOptions,
            tarifs:
              hotelRoomCategory.pricings && hotelRoomCategory.pricings.length
                ? hotelRoomCategory.pricings.map(
                    (pricing) =>
                      ({
                        name: pricing.name ?? undefined,
                        defaultPricesExist: pricing.defaultPricesExists
                          ? 'true'
                          : 'false',
                        defaultPrices: {
                          price: pricing.defaultAdultPrice ?? 0,
                          firstSpecialPrice:
                            pricing.defaultFirstSpecialPrice ?? undefined,
                          secondSpecialPrice:
                            pricing.defaultSecondSpecialPrice ?? undefined,
                        },
                        periods:
                          pricing.periods && pricing.periods.length
                            ? pricing.periods.map((period) => ({
                                startDate: period.startDate
                                  ? new Date(period.startDate)
                                  : undefined,
                                endDate: period.endDate
                                  ? new Date(period.endDate)
                                  : undefined,
                                applicationDays:
                                  period.applicationDays ?? WEEKDAYS,
                              }))
                            : [{ applicationDays: WEEKDAYS }],
                        pricePartitions: {
                          individual:
                            pricing.individualRoomRepartitions &&
                            pricing.individualRoomRepartitions.length
                              ? pricing.individualRoomRepartitions.map(
                                  (partition) => ({
                                    price: numberIfNotUndefined(
                                      partition.price,
                                    ),
                                    firstSpecialPrice: numberIfNotUndefined(
                                      partition.firstSpecialPrice,
                                    ),
                                    secondSpecialPrice: numberIfNotUndefined(
                                      partition.secondSpecialPrice,
                                    ),
                                    additionalPrice: numberIfNotUndefined(
                                      partition.additionalPrice,
                                    ),
                                  }),
                                )
                              : [],
                          double:
                            pricing.doubleRoomRepartitions &&
                            pricing.doubleRoomRepartitions.length
                              ? pricing.doubleRoomRepartitions.map(
                                  (partition) => ({
                                    price: numberIfNotUndefined(
                                      partition.price,
                                    ),
                                    firstSpecialPrice: numberIfNotUndefined(
                                      partition.firstSpecialPrice,
                                    ),
                                    secondSpecialPrice: numberIfNotUndefined(
                                      partition.secondSpecialPrice,
                                    ),
                                    additionalPrice: numberIfNotUndefined(
                                      partition.additionalPrice,
                                    ),
                                  }),
                                )
                              : [],
                          triple:
                            pricing.tripleRoomRepartitions &&
                            pricing.tripleRoomRepartitions.length
                              ? pricing.tripleRoomRepartitions.map(
                                  (partition) => ({
                                    price: numberIfNotUndefined(
                                      partition.price,
                                    ),
                                    firstSpecialPrice: numberIfNotUndefined(
                                      partition.firstSpecialPrice,
                                    ),
                                    secondSpecialPrice: numberIfNotUndefined(
                                      partition.secondSpecialPrice,
                                    ),
                                    additionalPrice: numberIfNotUndefined(
                                      partition.additionalPrice,
                                    ),
                                  }),
                                )
                              : [],
                          quadruple:
                            pricing.quadrupleRoomRepartitions &&
                            pricing.quadrupleRoomRepartitions.length
                              ? pricing.quadrupleRoomRepartitions.map(
                                  (partition) => ({
                                    price: numberIfNotUndefined(
                                      partition.price,
                                    ),
                                    firstSpecialPrice: numberIfNotUndefined(
                                      partition.firstSpecialPrice,
                                    ),
                                    secondSpecialPrice: numberIfNotUndefined(
                                      partition.secondSpecialPrice,
                                    ),
                                    additionalPrice: numberIfNotUndefined(
                                      partition.additionalPrice,
                                    ),
                                  }),
                                )
                              : [],
                        },
                      }) as const,
                  )
                : [
                    {
                      periods: [{ applicationDays: WEEKDAYS }],
                      pricePartitions: {
                        individual: [],
                        double: [],
                        triple: [],
                        quadruple: [],
                      },
                    },
                  ],
          }))
        : [
            {
              images: [],
              roomTypes: {
                individual: false,
                double: false,
                triple: false,
                quadruple: false,
              },
              individualRoomOptions: [0],
              doubleRoomOptions: [0],
              tripleRoomOptions: [],
              quadrupleRoomOptions: [],
              categoryPensionPricings:
                hotel.shouldFillOtherPensionTypes &&
                !hotel.areOtherPensionPricesCommon
                  ? [buildEmptyPensionCard()]
                  : [],
              tarifs: [
                {
                  periods: [{ applicationDays: WEEKDAYS }],
                  pricePartitions: {
                    individual: [],
                    double: [],
                    triple: [],
                    quadruple: [],
                  },
                },
              ],
            },
          ],
  }) as const;
